import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import CommitteesPage from './pages/public/CommitteesPage';
import NewsPage from './pages/public/NewsPage';
import EventsPage from './pages/public/EventsPage';
import GalleryPage from './pages/public/GalleryPage';
import ContactPage from './pages/public/ContactPage';
import InstitutionalMembershipForm from './pages/public/InstitutionalMembershipForm';
import InstitutionalDashboardPage from './pages/public/InstitutionalDashboardPage';
import GoverningBodyPage from './pages/public/GoverningBodyPage';
import ConclaveRegistration from './pages/public/ConclaveRegistration';
import AuthPage from './pages/public/AuthPage';
import ScholarshipsPage from './pages/public/ScholarshipsPage';
import DonorRegistrationPage from './pages/public/DonorRegistrationPage';
import DonorPayInstallmentPage from './pages/public/DonorPayInstallmentPage';
import ScholarshipApplicationPage from './pages/public/ScholarshipApplicationPage';

// New Public Pages
import PresenceOfSamajPage from './pages/public/PresenceOfSamajPage';
import BranchesAndNukhsPage from './pages/public/BranchesAndNukhsPage';
import MembersPage from './pages/public/MembersPage';
import ProgramsAndNewsPage from './pages/public/ProgramsAndNewsPage';
import FamiliesRegisterPage from './pages/public/FamiliesRegisterPage';
import FamilyRegistrationForm from './pages/public/FamilyRegistrationForm';
import MemberRegistrationPage from './pages/public/MemberRegistrationPage';
import FamilyEditPage from './pages/public/FamilyEditPage';
import MatrimonialPage from './pages/public/MatrimonialPage';
import ParichaySangamRegistration from './pages/public/ParichaySangamRegistration';
import VisionConventionRegistration from './pages/public/VisionConventionRegistration';
import PSLoginPage from './pages/public/PSLoginPage';
import PSRegisterPage from './pages/public/PSRegisterPage';
import PSDashboardPage from './pages/public/PSDashboardPage';
import PSVerifyIdPage from './pages/public/PSVerifyIdPage';
import PSScanPage from './pages/public/PSScanPage';
import TemplesPage from './pages/public/TemplesPage';
import ProfessionalsPage from './pages/public/ProfessionalsPage';
import ProfessionalRegistrationPage from './pages/public/ProfessionalRegistrationPage';
import ProfessionalLoginPage from './pages/public/ProfessionalLoginPage';
import ProfessionalDirectoryPage from './pages/public/ProfessionalDirectoryPage';
import PSGuestVolunteerRegisterPage from './pages/public/PSGuestVolunteerRegisterPage';
import PSGuestVolunteerCompletePage from './pages/public/PSGuestVolunteerCompletePage';
import PSMatrimonialDirectory from './pages/public/PSMatrimonialDirectory';
import OrganisationsPage from './pages/public/OrganisationsPage';
import HinglajKuldeviPage from './pages/public/HinglajKuldeviPage';
import UpattiOriginPage from './pages/public/UpattiOriginPage';
import HostelsPage from './pages/public/HostelsPage';
import DharamshalasPage from './pages/public/DharamshalasPage';
import SocialWelfareActivitiesPage from './pages/public/SocialWelfareActivitiesPage';
import ObjectsAndFunctionsPage from './pages/public/ObjectsAndFunctionsPage';
import HistoryPage from './pages/public/HistoryPage';

// Member Pages
import MemberDashboard from './pages/member/MemberDashboard';
import MemberDirectory from './pages/member/MemberDirectory';
import MemberProfile from './pages/member/MemberProfile';
import MembershipCard from './pages/member/MembershipCard';
import FeedbackPage from './pages/member/FeedbackPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminResetPassword from './pages/admin/AdminResetPassword';
import MemberManagement from './pages/admin/MemberManagement';
import CommitteeManagement from './pages/admin/CommitteeManagement';
import NewsManagement from './pages/admin/NewsManagement';
import EventManagement from './pages/admin/EventManagement';
import GalleryManagement from './pages/admin/GalleryManagement';
import DocumentManagement from './pages/admin/DocumentManagement';
import FeedbackManagement from './pages/admin/FeedbackManagement';
import ConclaveRegistrations from './pages/admin/ConclaveRegistrations';
import ScholarshipDonors from './pages/admin/ScholarshipDonors';
import ScholarshipApplications from './pages/admin/ScholarshipApplications';
import FamilyManagement from './pages/admin/FamilyManagement';
import ProfessionalManagement from './pages/admin/ProfessionalManagement';
import InstitutionalMembers from './pages/admin/InstitutionalMembers';
import ParichaySangamManagement from './pages/admin/ParichaySangamManagement';
import PSManagement from './pages/admin/PSManagement';
import PSCheckinDashboard from './pages/admin/PSCheckinDashboard';
import PSInterestActivity from './pages/admin/PSInterestActivity';
import PSReports from './pages/admin/PSReports';
import ProfessionalReports from './pages/admin/ProfessionalReports';
import GuestVolunteerManagement from './pages/admin/GuestVolunteerManagement';
import VisionConventionManagement from './pages/admin/VisionConventionManagement';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import FamilyReports from './pages/admin/FamilyReports';
import EmailCampaigns from './pages/admin/EmailCampaigns';
import CustomCityManager from './pages/admin/CustomCityManager';

// Layout
import PublicLayout from './layouts/PublicLayout';
import MemberLayout from './layouts/MemberLayout';
import AdminLayout from './layouts/AdminLayout';

// Auth Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Protected Route Components
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/member/dashboard" replace />;
  }

  if (user.status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-8 bg-card rounded-xl shadow-card">
          <h2 className="text-2xl font-serif font-bold text-primary mb-4">Membership Pending</h2>
          <p className="text-muted-foreground">
            Your membership application is under review. Please wait for admin approval.
          </p>
        </div>
      </div>
    );
  }

  if (user.status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-8 bg-card rounded-xl shadow-card">
          <h2 className="text-2xl font-serif font-bold text-destructive mb-4">Membership Rejected</h2>
          <p className="text-muted-foreground">
            Your membership application was not approved. Please contact admin for more information.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="presence-of-samaj" element={<PresenceOfSamajPage />} />
              <Route path="branches-and-nukhs" element={<BranchesAndNukhsPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="governing-body" element={<GoverningBodyPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="scholarship" element={<ScholarshipsPage />} />
              <Route path="scholarships" element={<ScholarshipsPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="programs-and-news" element={<ProgramsAndNewsPage />} />
              <Route path="families-register" element={<FamiliesRegisterPage />} />
              <Route path="matrimonial" element={<MatrimonialPage />} />
              <Route path="parichay-sangam/register" element={<Navigate to="/matrimonial" replace />} />
              <Route path="parichay-sangam/register-preview" element={<ParichaySangamRegistration />} />
              <Route path="parichay-sangam/login" element={<PSLoginPage />} />
              <Route path="parichay-sangam/directory" element={<PSMatrimonialDirectory />} />
              <Route path="vision-convention-2026" element={<VisionConventionRegistration />} />
              <Route path="temples" element={<TemplesPage />} />
              <Route path="organisations" element={<OrganisationsPage />} />
              <Route path="committees" element={<CommitteesPage />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="gallery" element={<GalleryPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="hinglaj-kuldevi" element={<HinglajKuldeviPage />} />
              <Route path="upatti-origin" element={<UpattiOriginPage />} />
              <Route path="hostels" element={<HostelsPage />} />
              <Route path="dharamshalas" element={<DharamshalasPage />} />
              <Route path="social-welfare-activities" element={<SocialWelfareActivitiesPage />} />
              <Route path="objects-and-functions" element={<ObjectsAndFunctionsPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="professionals" element={<ProfessionalsPage />} />
              <Route path="professional/directory" element={<ProfessionalDirectoryPage />} />
              <Route path="auth" element={<AuthPage />} />
            </Route>

            <Route path="/member-registration" element={<MemberRegistrationPage />} />
            <Route path="/institutional-membership" element={<InstitutionalMembershipForm />} />
            <Route path="/institutional/dashboard" element={<InstitutionalDashboardPage />} />
            {/* Conclave registration temporarily hidden — redirect to home */}
            <Route path="/conclave-registration" element={<Navigate to="/" replace />} />
            <Route path="/family-registration" element={<FamilyRegistrationForm />} />
            <Route path="/family/edit" element={<FamilyEditPage />} />
            <Route path="/scholarship/donor-registration" element={<DonorRegistrationPage />} />
            <Route path="/scholarship/donor/pay/:token" element={<DonorPayInstallmentPage />} />
            <Route path="/scholarship/application" element={<ScholarshipApplicationPage />} />
            <Route path="/professional/register" element={<ProfessionalRegistrationPage />} />
            <Route path="/professional/login" element={<ProfessionalLoginPage />} />
            <Route path="/professional/dashboard" element={<Navigate to="/professional/directory" replace />} />
            <Route path="/parichay-sangam/dashboard" element={<PSDashboardPage />} />
            <Route path="/parichay-sangam/guest-volunteer/register" element={<PSGuestVolunteerRegisterPage />} />
            <Route path="/parichay-sangam/guest-volunteer/complete/:token" element={<PSGuestVolunteerCompletePage />} />
            <Route path="/parichay-sangam/verify/:code" element={<PSVerifyIdPage />} />
            <Route path="/parichay-sangam/scan" element={<PSScanPage />} />

            {/* Member Routes */}
            <Route
              path="/member"
              element={
                <ProtectedRoute>
                  <MemberLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/member/dashboard" replace />} />
              <Route path="dashboard" element={<MemberDashboard />} />
              <Route path="directory" element={<MemberDirectory />} />
              <Route path="profile" element={<MemberProfile />} />
              <Route path="card" element={<MembershipCard />} />
              <Route path="feedback" element={<FeedbackPage />} />
            </Route>

            {/* Admin Login - Separate Page */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin-reset-password" element={<AdminResetPassword />} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="members" element={<MemberManagement />} />
              <Route path="committees" element={<CommitteeManagement />} />
              <Route path="news" element={<NewsManagement />} />
              <Route path="events" element={<EventManagement />} />
              <Route path="gallery" element={<GalleryManagement />} />
              <Route path="documents" element={<DocumentManagement />} />
              <Route path="feedback" element={<FeedbackManagement />} />
              <Route path="conclave-registrations" element={<ConclaveRegistrations />} />
              <Route path="scholarship-donors" element={<ScholarshipDonors />} />
              <Route path="scholarship-applications" element={<ScholarshipApplications />} />
              <Route path="families" element={<FamilyManagement />} />
              <Route path="professionals" element={<ProfessionalManagement />} />
              <Route path="institutional-members" element={<InstitutionalMembers />} />
              <Route path="parichay-sangam" element={<ParichaySangamManagement />} />
              <Route path="ps-attendees" element={<PSManagement attendingFilter="true" />} />
              <Route path="ps-non-attendees" element={<PSManagement attendingFilter="false" />} />
              <Route path="ps-profiles" element={<PSManagement />} />
              <Route path="ps-new-registration" element={<PSRegisterPage />} />
              <Route path="ps-reports" element={<PSReports />} />
              <Route path="ps-checkin" element={<PSCheckinDashboard />} />
              <Route path="ps-interest-activity" element={<PSInterestActivity />} />
              <Route path="professional-reports" element={<ProfessionalReports />} />
              <Route path="guest-volunteer" element={<GuestVolunteerManagement />} />
              <Route path="vision-convention" element={<VisionConventionManagement />} />
              <Route path="send-email" element={<EmailCampaigns />} />
              <Route path="custom-cities" element={<CustomCityManager />} />
              <Route path="admin-users" element={<AdminUserManagement />} />
              <Route path="family-reports" element={<FamilyReports />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
